import React, { useState } from "react";
import { toast } from "sonner";
import { User, Building2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { portalInputCls } from "@/components/portal/PortalBits";

function Row({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500 mb-2">{label}</div>
      {children}
    </label>
  );
}

function PersonalSection({ f, upd, clientEmail }) {
  return (
    <div className="border border-slate-200 bg-white">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <User className="h-4 w-4 text-primary-brand" />
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-slate-500">Personal Information</div>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Row label="Full name"><input value={f.name} onChange={upd("name")} className={portalInputCls} data-testid="prof-name" /></Row>
        <Row label="Email (read-only)"><input value={clientEmail} disabled className={portalInputCls + " opacity-60"} /></Row>
        <Row label="Phone"><input value={f.phone} onChange={upd("phone")} className={portalInputCls} data-testid="prof-phone" /></Row>
        <Row label="Job title"><input value={f.job_title} onChange={upd("job_title")} className={portalInputCls} data-testid="prof-job" /></Row>
        <Row label="Preferred contact">
          <select value={f.preferred_contact} onChange={upd("preferred_contact")} className={portalInputCls} data-testid="prof-contact">
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="sms">SMS</option>
          </select>
        </Row>
      </div>
    </div>
  );
}

function CompanySection({ f, upd }) {
  return (
    <div className="border border-slate-200 bg-white">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <Building2 className="h-4 w-4 text-primary-brand" />
        <div className="text-xs uppercase tracking-[0.25em] font-bold text-slate-500">Company Information</div>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        <Row label="Company name"><input value={f.company_name} onChange={upd("company_name")} className={portalInputCls} data-testid="prof-company" /></Row>
        <Row label="Business type"><input value={f.business_type} onChange={upd("business_type")} className={portalInputCls} data-testid="prof-btype" /></Row>
        <Row label="Company address" full><input value={f.company_address} onChange={upd("company_address")} className={portalInputCls} data-testid="prof-addr" /></Row>
        <Row label="City"><input value={f.city} onChange={upd("city")} className={portalInputCls} data-testid="prof-city" /></Row>
        <Row label="State"><input value={f.state} onChange={upd("state")} className={portalInputCls} data-testid="prof-state" /></Row>
        <Row label="ZIP code"><input value={f.zip_code} onChange={upd("zip_code")} className={portalInputCls} data-testid="prof-zip" /></Row>
        <Row label="EIN / Tax ID"><input value={f.ein} onChange={upd("ein")} className={portalInputCls} data-testid="prof-ein" /></Row>
        <Row label="Website" full><input value={f.website} onChange={upd("website")} className={portalInputCls} data-testid="prof-website" /></Row>
      </div>
    </div>
  );
}

export function ProfileForm({ client, onSaved }) {
  const [f, setF] = useState({
    name: client.name || "", phone: client.phone || "", job_title: client.job_title || "",
    preferred_contact: client.preferred_contact || "email",
    company_name: client.company_name || "", business_type: client.business_type || "",
    company_address: client.company_address || "", city: client.city || "",
    state: client.state || "", zip_code: client.zip_code || "",
    ein: client.ein || "", website: client.website || "",
  });
  const [loading, setLoading] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.patch("/client/me", f);
      onSaved(data);
    } catch (error) {
      console.error("Profile save failed:", error);
      toast.error("Save failed");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={submit} data-testid="portal-profile-form" className="space-y-6">
      <PersonalSection f={f} upd={upd} clientEmail={client.email} />
      <CompanySection f={f} upd={upd} />
      <div className="flex justify-end">
        <button type="submit" disabled={loading} data-testid="prof-save"
          className="inline-flex items-center gap-2 bg-primary-brand hover:bg-[#E04F00] disabled:opacity-60 text-white px-8 py-3 font-bold uppercase tracking-wider text-sm">
          <Save className="h-4 w-4" /> {loading ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </form>
  );
}
